import GLightbox from "glightbox";
import { initAccordions } from "./_accordion";
import { initSliders, destroySliders } from "./_slider";

/**
 * 汎用モーダル（GLightbox / 任意のHTMLコンテンツ）
 *
 * マークアップ規約
 *   <!-- トリガー：スライドでもボタンでもリンクでもよい -->
 *   <button type="button" data-modal="benefit-01">…</button>
 *
 *   <!-- モーダルの中身。<template> なので描画されず、CSSもJSも干渉しない -->
 *   <template data-modal-content="benefit-01">
 *     <div class="modal"> 画像 / 見出し / 本文 / ボタン / テーブル / アコーディオン … </div>
 *   </template>
 *
 * GLightbox の type:"inline" は元DOMを複製する挙動に依存するため使わず、
 * <template> の中身を elements[].content として渡す。
 * トリガーの個数・並び順にのみ依存するので、項目が増減してもコードは変えない。
 */

const TRIGGER_SELECTOR = "[data-modal]";

/** CSSのスコープ用。GLightbox のコンテナに .glightbox-fanclub-modal が付く */
const SKIN = "fanclub-modal";

/** モーダル本体の既定サイズ。data-modal-width / data-modal-height で個別に上書きできる */
const DEFAULT_WIDTH = "min(92vw, 1040px)";
const DEFAULT_HEIGHT = "auto";

/** 生成済みインスタンス（ページに1つ） */
let lightbox = null;

/** 直前にモーダルを開いたトリガー。閉じたときにフォーカスを戻す */
let lastTrigger = null;

/**
 * トリガーに対応する <template> を探す
 *
 * @param {Document|HTMLElement} root
 * @param {String} key - data-modal の値
 * @return {HTMLTemplateElement|null}
 */
const findTemplate = (root, key) => {
  const selector = `template[data-modal-content="${key}"]`;
  return root.querySelector?.(selector) ?? document.querySelector(selector);
};

/**
 * 閉じるボタンのSVG（Figma: close / 「＋」を45度回して「×」にしている）
 */
const CLOSE_ICON = `<svg viewBox="0 0 34.382 34.382" fill="none" aria-hidden="true" focusable="false">
  <path d="M2 17.191H32.382M17.191 2V32.382" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

/**
 * スクロールバー幅を --scrollbar に保持する。
 * モーダル表示中は overflow:hidden で幅が変わるため測らない。
 *
 * @return {void}
 */
const syncScrollbarWidth = () => {
  const root = document.documentElement;
  if (root.classList.contains("glightbox-open")) return;
  const width = Math.max(0, window.innerWidth - root.clientWidth);
  root.style.setProperty("--scrollbar", `${width}px`);
};

/**
 * カードの右上に閉じるボタンを差し込む
 * GLightbox 標準の .gclose は画面の右上に固定されてしまい、Figma の
 * 「カードの角に重ねる」配置にできないため、中身側に自前で置く。
 *
 * @param {HTMLElement} slide
 * @param {Object} instance - GLightbox インスタンス
 * @return {void}
 */
const appendCloseButton = (slide, instance) => {
  const modal = slide.querySelector(".modal");
  if (!modal || modal.querySelector(".modal__close")) return;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "modal__close";
  button.setAttribute("aria-label", "閉じる");
  button.innerHTML = CLOSE_ICON;
  button.addEventListener("click", () => instance?.close());

  modal.prepend(button);
};

/**
 * モーダルに挿入されたDOMを初期化する
 * GLightbox は中身を新しいノードとして挿入するため、開いた後に必ず通す必要がある
 *
 * @param {HTMLElement} slide
 * @return {void}
 */
const enhanceSlide = (slide) => {
  if (!slide) return;

  appendCloseButton(slide, lightbox);
  initAccordions(slide);
  initSliders(slide);
};

/**
 * モーダルを初期化する（冪等）
 *
 * @param {Document|HTMLElement} root
 * @param {Object} [callbacks]
 * @param {Function} [callbacks.onOpen]
 * @param {Function} [callbacks.onClose]
 * @return {Object|null} GLightbox インスタンス
 */
export const initModals = (root = document, { onOpen, onClose } = {}) => {
  const triggers = [...(root.querySelectorAll?.(TRIGGER_SELECTOR) ?? [])];
  if (!triggers.length) return null;

  const elements = [];
  const openable = [];

  triggers.forEach((trigger) => {
    if (trigger.dataset.modalReady === "true") return;

    const template = findTemplate(root, trigger.dataset.modal);
    if (!template) {
      console.warn(`[modal] template[data-modal-content="${trigger.dataset.modal}"] が見つかりません`);
      return;
    }

    elements.push({
      content: template.innerHTML,
      width: trigger.dataset.modalWidth || DEFAULT_WIDTH,
      height: trigger.dataset.modalHeight || DEFAULT_HEIGHT,
    });
    openable.push(trigger);
  });

  if (!elements.length) return lightbox;

  syncScrollbarWidth();
  window.addEventListener("resize", syncScrollbarWidth);

  lightbox = GLightbox({
    elements,
    skin: SKIN,
    loop: false,
    zoomable: false,
    draggable: false,
    touchNavigation: false,
    keyboardNavigation: true,
    closeButton: true,
    closeOnOutsideClick: true,
    // カードは CSS 側でフェード＋下から上へ（_modal.scss の modal-pop-in）
    openEffect: "fade",
    closeEffect: "fade",
    slideEffect: "fade",
    onOpen: () => {
      const modal = document.querySelector(`.glightbox-${SKIN}`);
      if (modal) {
        modal.setAttribute("role", "dialog");
        modal.setAttribute("aria-modal", "true");
      }
      onOpen?.();
    },
    afterSlideLoad: ({ slide }) => enhanceSlide(slide),
    slideInserted: ({ slide }) => enhanceSlide(slide),
    onClose: () => {
      const modal = document.querySelector(`.glightbox-${SKIN}`);
      if (modal) destroySliders(modal);
      onClose?.();
      lastTrigger?.focus?.();
      lastTrigger = null;
    },
  });

  openable.forEach((trigger, index) => {
    trigger.setAttribute("aria-haspopup", "dialog");
    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      lastTrigger = trigger;
      syncScrollbarWidth();
      lightbox.openAt(index);
    });
    trigger.dataset.modalReady = "true";
  });

  return lightbox;
};

/**
 * モーダルインスタンスを取得する
 *
 * @return {Object|null}
 */
export const getModal = () => lightbox;
